import React, { useEffect, useState } from "react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectAccountOnboarding, ConnectComponentsProvider } from "@stripe/react-connect-js";
import Skeleton from "../../components/Skeleton/Skeleton";
import { TransactionSummaryRowSkeleton } from "../../components/Skeletons/FinanceListSkeletons";
import { useDispatch, useSelector } from "react-redux";
import {
    clearTopNavbar,
    setTopNavbar,
} from "../../Reducers/actions/navbarActions";
import TransactionSummary from "./TransactionSummary";
import "./Withdrawals.scss";
import { useNavigate } from "react-router-dom";
import WithdrawalSection from "../../components/WithdrawalSection/WithdrawalSection";
import WithdrawalSuccess from "../../components/WithdrawalSection/WithdrawalSuccess";
import AddBankAccountSection from "../../components/AddBankAccountSection/AddBankAccountSection";
import { HYK_FEE_PERCENT_WITHDRAWAL } from "../../constants/globals";
import {
    completeOnboarding,
    getAccount,
    getAccountOnboardingLink,
    getAccountSession,
    getBalance,
    getBankAccount,
    getKycStatus,
    getPaginatedPayouts,
    refreshCapabilities,
} from "../../Reducers/actions/accountActions";
import { fromCentsToPounds } from "../../../helpers/money";
import { buildOnboardingPayloadFromMainAddress } from "../../../helpers/stripeOnboardingPayload";
import OverlayNotification from "../../components/OverlayNotifcation/OverlayNotification";
import Icon from "../../components/Icon/Icon";

// Onboarding failures where retrying setup won't help — the user has to fix
// their address (country) first. COUNTRY_MISMATCH means the Stripe account
// already exists in another country (immutable), so support has to step in.
const ADDRESS_FIX_REQUIREMENT_CODES = ["COUNTRY_REQUIRED", "UNSUPPORTED_COUNTRY"];

function WithdrawalsScreenSkeleton() {

    return (
        <div className="withdrawals-container p-16" role="status" aria-label="Loading withdrawals">
            <div className="balance-container pb-24">
                <div className="d-flex aic jcsb">
                    <div>
                        <Skeleton width="160px" height="12px" borderRadius="6px" className="m-0 p-0" />
                        <Skeleton width="140px" height="36px" borderRadius="8px" className="m-0 p-0 pt-8" />
                    </div>
                    <Skeleton width="112px" height="64px" borderRadius="8px" className="m-0 p-0" />
                </div>
                <Skeleton width="140px" height="36px" borderRadius="8px" className="m-0 p-0 mt-16" />
            </div>
            <div className="fees-info-container">
                <Skeleton width="220px" height="16px" borderRadius="8px" className="m-0 p-0 pt-12" />
            </div>
            <div className="transactions-container py-24">
                <div className="d-flex aic jcsb mb-8">
                    <Skeleton width="180px" height="14px" borderRadius="6px" className="m-0 p-0" />
                    <Skeleton width="64px" height="14px" borderRadius="6px" className="m-0 p-0" />
                </div>
                {[1, 2, 3].map((i) => (
                    <TransactionSummaryRowSkeleton key={i} />
                ))}
            </div>
        </div>
    );
}

function Withdrawals() {
    const [showWithdrawal, setShowWithdrawal] = useState(false);
    const [showSuccessWithdrawal, setShowSuccessWithdrawal] = useState(false);
    const [showAddBankAccount, setShowAddBankAccount] = useState(false);
    const [showKycEmailSuccess, setShowKycEmailSuccess] = useState(false);
    const [payoutRequirement, setPayoutRequirement] = useState(null);
    const [connectInstance, setConnectInstance] = useState(null);
    const [setupLoading, setSetupLoading] = useState(false);
    const [fullKycLinkLoading, setFullKycLinkLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        balance,
        balanceLoading,
        payoutsLoading,
        payouts,
        bankAccountLoading,
        bankAccount,
        account,
        accountLoading,
        withdrawDisabledReason,
    } = useSelector((state) => ({
        account: state?.account?.account,
        accountLoading: state?.account?.accountLoading,
        balance: state?.account?.balance,
        payouts: state?.account?.payouts,
        bankAccount: state?.account?.bankAccount,
        balanceLoading: state?.account?.balanceLoading,
        payoutsLoading: state?.account?.payoutsLoading,
        bankAccountLoading: state?.account?.bankAccountLoading,
        withdrawDisabledReason: state?.account?.withdrawDisabledReason,
    }));

    useEffect(() => {
        dispatch(
            setTopNavbar({
                title: "Withdrawals",
                showBackArrow: true,
            })
        );
        dispatch(getBalance());
        dispatch(getPaginatedPayouts(10));
        dispatch(getBankAccount());
        dispatch(getAccount());

        return () => {
            dispatch(clearTopNavbar());
        };
    }, []);

    const handleViewAllTransactions = () => {
        navigate("/withdrawals/transactions");
    };

    const handleWithdrawalClick = () => {
        setShowWithdrawal(true);
    };

    // const handleAddBankAccountClick = () => {
    //     setShowAddBankAccount(true);
    // };

    const handleSendKycEmail = () => {
        if (!account?.id) {
            setPayoutRequirement({
                code: "ACCOUNT_NOT_SETUP",
                message: "Please complete your account onboarding before verification.",
            });
            return;
        }
        getAccountOnboardingLink(account?.id)
            .then((res) => {
                if (res.success) {
                    setPayoutRequirement(null);
                    setShowKycEmailSuccess(true);
                    return;
                }
                setPayoutRequirement({
                    code: res?.code || "STRIPE_ACCOUNT_LINK_UNAVAILABLE",
                    message:
                        res?.message ||
                        "We couldn't generate a verification link for this account. Please retry onboarding.",
                });
            })
            .catch((err) => {
                console.error(err);
                setPayoutRequirement({
                    code: "STRIPE_ACCOUNT_LINK_UNAVAILABLE",
                    message:
                        "We couldn't generate a verification link for this account. Please retry onboarding.",
                });
            });
    };

    const handleOpenFullKycLink = async () => {
        if (!account?.id) {
            setPayoutRequirement({
                code: "ACCOUNT_NOT_SETUP",
                message: "Please complete your account onboarding before verification.",
            });
            return;
        }
        setFullKycLinkLoading(true);
        try {
            const response = await getAccountOnboardingLink(account.id, { fullKyc: true });
            if (!response?.success || !response?.data?.url) {
                setPayoutRequirement({
                    code: response?.code || "STRIPE_ACCOUNT_LINK_UNAVAILABLE",
                    message:
                        response?.message ||
                        "We couldn't open Stripe verification right now. Please try again.",
                });
                return;
            }
            window.open(response.data.url, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("Failed to open full KYC link:", error);
            setPayoutRequirement({
                code: "STRIPE_ACCOUNT_LINK_UNAVAILABLE",
                message:
                    "We couldn't open Stripe verification right now. Please try again.",
            });
        } finally {
            setFullKycLinkLoading(false);
        }
    };

    const initializeConnectOnboarding = async (accountId) => {
        if (!accountId || !import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) return;
        const instance = loadConnectAndInitialize({
            publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
            fetchClientSecret: async () => {
                const sessionResponse = await getAccountSession(accountId);
                if (!sessionResponse?.success || !sessionResponse?.data?.client_secret) {
                    throw new Error("Unable to initialize Stripe onboarding session");
                }
                return sessionResponse.data.client_secret;
            },
        });
        setConnectInstance(instance);
    };

    const handlePayoutRequirement = async (response) => {
        setShowWithdrawal(false);
        setPayoutRequirement(response || null);

        if (
            response?.code === "FULL_KYC_REQUIRED" &&
            account?.id &&
            import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
        ) {
            await initializeConnectOnboarding(account.id);
        }
    };

    const handleEmbeddedOnboardingExit = async () => {
        try {
            await refreshCapabilities();
        } catch (err) {
            console.error("Failed to refresh capabilities:", err);
        }

        dispatch(getBalance());
        dispatch(getAccount());
        dispatch(getBankAccount());
        dispatch(getKycStatus());
        setPayoutRequirement(null);
        setConnectInstance(null);
    };

    const handleStartAccountSetup = async () => {
        setSetupLoading(true);
        try {
            // The Stripe account country is immutable — only create an account
            // with an explicit country taken from the user's main address.
            const onboardingPayload = account?.id
                ? {}
                : await buildOnboardingPayloadFromMainAddress();
            if (!account?.id && !onboardingPayload?.country) {
                setPayoutRequirement({
                    code: "COUNTRY_REQUIRED",
                    message:
                        "Add your address (including your country) before setting up payouts.",
                });
                return;
            }

            const response = await completeOnboarding(onboardingPayload || {});
            if (!response?.success || !response?.data?.account_id) {
                setPayoutRequirement({
                    code: response?.code || "ACCOUNT_SETUP_FAILED",
                    message: response?.message || "Unable to set up payout account.",
                });
                return;
            }

            await initializeConnectOnboarding(response.data.account_id);
            setPayoutRequirement({
                code: "FULL_KYC_REQUIRED",
                message: "Identity verification is required before your first withdrawal.",
            });
            dispatch(getAccount());
            dispatch(getKycStatus());
        } catch (error) {
            console.error("Account setup failed:", error);
            setPayoutRequirement({
                code: error?.response?.code || "ACCOUNT_SETUP_FAILED",
                message:
                    error?.response?.message ||
                    "Unable to set up payout account. Please try again.",
            });
        } finally {
            setSetupLoading(false);
        }
    };


    const isLoading =
        balanceLoading ||
        bankAccountLoading ||
        accountLoading;
    if (isLoading) {
        return <WithdrawalsScreenSkeleton />;
    }

    const hasPayouts = Array.isArray(payouts?.data) && payouts.data.length > 0;

    return (
        <div className="withdrawals-container p-16">
            {showWithdrawal && (
                <WithdrawalSection
                    handleSuccess={() => {
                        setShowWithdrawal(false);
                        setShowSuccessWithdrawal(true);
                        setPayoutRequirement(null);
                        dispatch(getPaginatedPayouts(10));
                    }}
                    onPayoutRequirement={handlePayoutRequirement}
                    onClose={() => setShowWithdrawal(false)}
                    lastFourDigits={bankAccount?.last4}
                    bankAccountId={bankAccount?.id}
                />
            )}
            {showKycEmailSuccess && (
                <OverlayNotification
                    onClose={() => {
                        setShowKycEmailSuccess(false);
                        setPayoutRequirement(null);
                    }}
                    email={account?.email}
                />
            )}
            {showSuccessWithdrawal && (
                <WithdrawalSuccess onClose={() => setShowSuccessWithdrawal(false)} />
            )}
            {showAddBankAccount && (
                <AddBankAccountSection onClose={() => setShowAddBankAccount(false)} />
            )}
            {payoutRequirement?.code === "KYC_REQUIRED" && (
                <div
                    className="mb-12 p-12"
                    style={{ border: "1px solid #FFB020", borderRadius: "12px", background: "#FFF8E5" }}
                    role="status"
                    aria-live="polite"
                >
                    <p className="fs-xs fw-medium fg-dark m-0">
                        Basic verification is required before withdrawal.
                    </p>
                    <button
                        type="button"
                        onClick={handleSendKycEmail}
                        className="btn btn-sm red-btn mt-8 px-24 fs-tiny fw-extra-bold"
                        aria-label="Start basic verification"
                    >
                        Verify identity
                    </button>
                </div>
            )}
            {payoutRequirement?.code === "FULL_KYC_REQUIRED" && (
                <div
                    className="mb-12 p-12"
                    style={{ border: "1px solid #FFB020", borderRadius: "12px", background: "#FFF8E5" }}
                    role="status"
                    aria-live="polite"
                >
                    <p className="fs-xs fw-medium fg-dark m-0">
                        Identity verification is required before your first withdrawal.
                    </p>
                    {connectInstance ? (
                        <div className="mt-12">
                            <ConnectComponentsProvider connectInstance={connectInstance}>
                                <ConnectAccountOnboarding onExit={handleEmbeddedOnboardingExit} />
                            </ConnectComponentsProvider>
                        </div>
                    ) : (
                        <div className="mt-8">
                            <p className="fs-tiny fg-grey7 mb-0">
                                Preparing verification flow...
                            </p>
                            <button
                                type="button"
                                onClick={handleOpenFullKycLink}
                                className="btn btn-sm red-btn mt-8 px-24 fs-tiny fw-extra-bold"
                                disabled={fullKycLinkLoading}
                                aria-label="Open Stripe verification page"
                            >
                                {fullKycLinkLoading ? "Opening..." : "Open Stripe verification page"}
                            </button>
                        </div>
                    )}
                </div>
            )}
            {payoutRequirement &&
                payoutRequirement.code !== "KYC_REQUIRED" &&
                payoutRequirement.code !== "FULL_KYC_REQUIRED" && (
                    <div
                        className="mb-12 p-12"
                        style={{ border: "1px solid #FFB020", borderRadius: "12px", background: "#FFF8E5" }}
                        role="status"
                        aria-live="polite"
                    >
                        <p className="fs-xs fw-medium fg-dark m-0">
                            {payoutRequirement.message ||
                                "Verification is required before using withdrawals."}
                        </p>
                        {ADDRESS_FIX_REQUIREMENT_CODES.includes(payoutRequirement.code) ? (
                            <button
                                type="button"
                                onClick={() => navigate("/shipping-addresses")}
                                className="btn btn-sm red-btn mt-8 px-24 fs-tiny fw-extra-bold"
                                aria-label="Update your address"
                            >
                                Update address
                            </button>
                        ) : payoutRequirement.code === "COUNTRY_MISMATCH" ? null : (
                            <button
                                type="button"
                                onClick={handleStartAccountSetup}
                                className="btn btn-sm red-btn mt-8 px-24 fs-tiny fw-extra-bold"
                                aria-label="Set up payout account"
                                disabled={setupLoading}
                            >
                                {setupLoading ? "Preparing verification..." : "Set up payout account"}
                            </button>
                        )}
                    </div>
                )}
            <div className="balance-container pb-24">
                <div className="d-flex aic jcsb">
                    <div>
                        <div className="balance-title">AVAILABLE BALANCE</div>
                        <div className="balance-amount pt-8">
                            {fromCentsToPounds(
                                balance?.available?.amount ?? balance?.amount ?? 0
                            )}
                        </div>
                    </div>
                    <div className="">
                        <button
                            onClick={handleWithdrawalClick}
                            disabled={
                                !(balance?.available?.amount ?? balance?.amount) ||
                                !bankAccount?.id
                            }
                            className="btn red-btn py-32 px-24 d-flex aic jcc"
                            aria-label={
                                withdrawDisabledReason
                                    ? withdrawDisabledReason
                                    : "Withdraw"
                            }
                        >
                            Withdraw
                        </button>
                    </div>
                </div>
                {withdrawDisabledReason && (
                    <div
                        className="withdraw-disabled-message mt-12 p-12 fs-tiny fg-dark"
                        role="status"
                        aria-live="polite"
                    >
                        {withdrawDisabledReason}
                    </div>
                )}
                <div className="d-flex aic">
                    {!bankAccount?.id ? (
                        <button
                            onClick={handleSendKycEmail}
                            className="btn btn-sm red-btn mt-16 mr-8 px-24 fs-tiny fw-extra-bold "
                        >
                            verify identity
                        </button>) : null}
                </div>
            </div>
            <div className="fees-info-container">
                <div className="fees-info-title pt-12 d-flex f-row">
                    <p>Withdrawal fee: {HYK_FEE_PERCENT_WITHDRAWAL * 100}% </p><Icon alias={"warning_fees"} color={"#FF8CDC"} height={16} width={16} />

                </div>
            </div>
            {!payoutsLoading && hasPayouts &&
                <div className="transactions-container py-24">
                    <div className="d-flex aic jcsb mb-8">
                        <div className="transactions-title">Recent Transactions</div>
                        <div
                            onClick={handleViewAllTransactions}
                            className="transactions-action"
                        >
                            View All
                        </div>
                    </div>
                    {payoutsLoading ? (
                        <>
                            {[1, 2, 3].map((i) => (
                                <TransactionSummaryRowSkeleton key={i} />
                            ))}
                        </>
                    ) : (
                        payouts?.data?.map((transaction) => (
                            <TransactionSummary transaction={transaction} />
                        ))
                    )}
                </div>}
        </div>
    );
}

export default Withdrawals;
